import { useLayer } from '@statsig/react-bindings';
import { useCallback } from 'react';

import { Statsig } from '../statsig/StatsigProvider';
import { ExperimentLayers } from '../statsig/experiments';
import { AdmissionFlow } from '../types';
import { VideoPlatform, normalizeVideoPlatform } from '../video-sdk/videoPlatform';
import {
  useGetVideoPlatformOfAdmittedRoomCallback,
  useVideoPlatformOfAdmittedRoom,
} from '../visitor/waiting-room/admission/useVideoPlatformOfAdmittedRoom';
import { useVideoPlatform, useVideoPlatformCallback } from './useVideoPlatform';

const [admissionLayerName, { automaticAdmissionIsEnabled }] = ExperimentLayers.Admission;

function getAdmissionFlow({
  videoPlatformOfAdmittedRoom,
  currentVideoPlatform,
  isAutomaticAdmissionEnabled,
}: {
  videoPlatformOfAdmittedRoom: VideoPlatform | null;
  currentVideoPlatform: VideoPlatform | null;
  isAutomaticAdmissionEnabled: boolean;
}): AdmissionFlow {
  const videoPlatform = videoPlatformOfAdmittedRoom ?? normalizeVideoPlatform(currentVideoPlatform);
  return videoPlatform === VideoPlatform.Zoom && !isAutomaticAdmissionEnabled ? 'manual' : 'auto';
}

export function useIsAutomaticAdmissionEnabled(): boolean {
  return useLayer(admissionLayerName).get<boolean>(automaticAdmissionIsEnabled, false) ?? false;
}

/**
 * The client user reads the admission experiment and relays their admission flow
 * to the provider side as part of their presence updates.
 *
 * Defaults to manual admission for Zoom (Control), auto otherwise.
 */
export function useAdmissionFlow(): AdmissionFlow {
  const isAutomaticAdmissionEnabled = useIsAutomaticAdmissionEnabled();
  const videoPlatformOfAdmittedRoom = useVideoPlatformOfAdmittedRoom();
  const currentVideoPlatform = useVideoPlatform();

  return getAdmissionFlow({
    videoPlatformOfAdmittedRoom,
    currentVideoPlatform,
    isAutomaticAdmissionEnabled,
  });
}

export function useAdmissionFlowCallback(): () => AdmissionFlow {
  const getVideoPlatformOfAdmittedRoom = useGetVideoPlatformOfAdmittedRoomCallback();
  const getVideoPlatform = useVideoPlatformCallback();

  return useCallback(() => {
    const isAutomaticAdmissionEnabled = Statsig.getLayer(admissionLayerName).get<boolean>(
      automaticAdmissionIsEnabled,
      false,
    );
    const videoPlatformOfAdmittedRoom = getVideoPlatformOfAdmittedRoom();
    const currentVideoPlatform = getVideoPlatform();
    return getAdmissionFlow({
      videoPlatformOfAdmittedRoom,
      currentVideoPlatform,
      isAutomaticAdmissionEnabled,
    });
  }, [getVideoPlatformOfAdmittedRoom, getVideoPlatform]);
}
